import {
  STREAM_EVENT,
  type StreamedProgressiveUiComponentData,
} from 'tripPlanner/api/types'
import type { MessagePart } from 'tripPlanner/components/Chat/Message'
import type { StreamEvent } from 'tripPlanner/utils/streamEventParser'
import {
  isStreamedProgressiveUiComponentData,
} from 'tripPlanner/utils/streamedProgressiveUi'
import {
  applyStreamedUiPartPatch,
  completeStreamedUiPart,
} from './streamedUiPartAdapters'

type ProgressiveUiStreamEvent = Extract<
  StreamEvent,
  {
    event:
      | typeof STREAM_EVENT.UI_PART_STARTED
      | typeof STREAM_EVENT.UI_PART_PATCH
      | typeof STREAM_EVENT.UI_PART_COMPLETED
      | typeof STREAM_EVENT.UI_PART_FAILED
  }
>

interface StreamedUiPartsState {
  partIndexes: Map<string, number>
}

interface ApplyProgressiveUiPartEventArgs {
  event: ProgressiveUiStreamEvent
  expectedMessageId: string
  parts: Array<MessagePart>
  state: StreamedUiPartsState
  onPartFailed?: (uiType: StreamedProgressiveUiComponentData['type'], reason: 'server_failed') => void
}

interface ApplyProgressiveUiPartEventResult {
  parts: Array<MessagePart>
  changed: boolean
}

export function createStreamedUiPartsState(): StreamedUiPartsState {
  return { partIndexes: new Map() }
}

export function resetStreamedUiPartsState(state: StreamedUiPartsState): void {
  state.partIndexes = new Map()
}

function rebuildStreamedUiPartIndexes(state: StreamedUiPartsState, parts: Array<MessagePart>): void {
  state.partIndexes = new Map()
  parts.forEach((part, index) => {
    if (part.type === 'ui_component' && isStreamedProgressiveUiComponentData(part.data)) {
      state.partIndexes.set(part.data.streamedPartId, index)
    }
  })
}

function getStreamedUiPartIndex(state: StreamedUiPartsState, parts: Array<MessagePart>, partId: string): number | undefined {
  const index = state.partIndexes.get(partId)
  if (index === undefined) return undefined
  const part = parts[index]
  if (part?.type !== 'ui_component' || !isStreamedProgressiveUiComponentData(part.data)) return undefined
  return index
}

export function applyProgressiveUiPartEvent({
  event,
  expectedMessageId,
  parts,
  state,
  onPartFailed,
}: ApplyProgressiveUiPartEventArgs): ApplyProgressiveUiPartEventResult {
  if (event.messageId !== expectedMessageId) return { parts, changed: false }

  if (event.event === STREAM_EVENT.UI_PART_STARTED) {
    if (state.partIndexes.has(event.partId)) return { parts, changed: false }

    const data: StreamedProgressiveUiComponentData = {
      ...event.data,
      streamedPartId: event.partId,
      streamingStatus: 'loading',
    }
    state.partIndexes.set(event.partId, parts.length)
    parts.push({ type: 'ui_component', data })
    return { parts, changed: true }
  }

  const index = getStreamedUiPartIndex(state, parts, event.partId)
  if (index === undefined) return { parts, changed: false }

  const part = parts[index]
  if (part?.type !== 'ui_component' || !isStreamedProgressiveUiComponentData(part.data)) {
    return { parts, changed: false }
  }

  if (event.event === STREAM_EVENT.UI_PART_PATCH) {
    const patchedData = applyStreamedUiPartPatch(part.data, event)
    if (patchedData) {
      parts[index] = {
        type: 'ui_component',
        data: patchedData,
      }
      return { parts, changed: true }
    }

    return { parts, changed: false }
  }

  if (event.event === STREAM_EVENT.UI_PART_COMPLETED) {
    if (event.data.type !== part.data.type) {
      console.warn(`[trip-chat] Progressive UI completion type mismatch for partId ${event.partId}: expected ${part.data.type}, got ${event.data.type}`)
      return { parts, changed: false }
    }

    const completedData = completeStreamedUiPart(part.data, event)
    if (!completedData) return { parts, changed: false }

    parts[index] = {
      type: 'ui_component',
      data: completedData,
    }
    return { parts, changed: true }
  }

  onPartFailed?.(part.data.type, 'server_failed')
  parts.splice(index, 1)
  rebuildStreamedUiPartIndexes(state, parts)
  return { parts, changed: true }
}
